import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Trash2, Save, Wallet, CalendarCheck, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const TX_LABELS = {
  payment: "Paiement", bonus: "Bonus", birthday: "Anniversaire", welcome: "Bienvenue",
  first_reservation: "1ère réservation", manual_add: "Ajout manuel", manual_remove: "Retrait manuel",
  redeem: "Échange récompense", refund: "Remboursement",
};

const STATUS_COLORS = {
  confirmee: "text-[#009246]", en_attente: "text-[#D4AF37]", annulee: "text-gray-500", refusee: "text-[#ff6b81]",
};

export const AdminCustomerFiche = ({ userId, onClose, onChanged }) => {
  const [fiche, setFiche] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/customers/${userId}/fiche`).then((r) => {
      setFiche(r.data);
      setNotes(r.data.user.admin_notes || "");
    }).catch(() => toast.error("Impossible de charger la fiche"));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const toggleConsent = async (field) => {
    try {
      await api.put(`/admin/customers/${userId}`, { [field]: !fiche.user[field] });
      load();
      toast.success("Consentement mis à jour");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/admin/customers/${userId}`, { admin_notes: notes });
      toast.success("Notes enregistrées");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteGdpr = async () => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT toutes les données de ce client (RGPD) ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/admin/customers/${userId}`);
      toast.success("Données du client supprimées (RGPD)");
      onChanged?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!fiche) return null;
  const { user, balance, reservations, upcoming, ritas_history } = fiche;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" data-testid="customer-fiche-overlay" onClick={onClose}>
      <div className="w-full max-w-2xl h-full overflow-y-auto bg-[#0a0a0a] border-l border-white/10 p-6 lg:p-8" data-testid="customer-fiche" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-serif-elegant text-2xl font-bold">{user.first_name} {user.last_name}</h2>
            <p className="text-sm text-gray-400 mt-1">{user.email} {user.phone ? `· ${user.phone}` : ""}</p>
            <p className="text-xs text-gray-500 mt-1">
              Inscrit le {user.created_at?.split("T")[0]} · {user.auth_provider === "google" ? "Google" : "Email"}
              {user.birthday ? ` · Anniversaire : ${user.birthday}` : ""}
            </p>
          </div>
          <button data-testid="fiche-close-btn" onClick={onClose} className="p-2 text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="ic-card !transform-none p-4 text-center">
            <Wallet size={16} className="text-[#D4AF37] mx-auto mb-2" />
            <p className="text-2xl font-semibold text-[#D4AF37]" data-testid="fiche-ritas-balance">{balance}</p>
            <p className="text-[11px] text-gray-500">RITAS</p>
          </div>
          <div className="ic-card !transform-none p-4 text-center">
            <CalendarCheck size={16} className="text-[#D4AF37] mx-auto mb-2" />
            <p className="text-2xl font-semibold">{reservations.length}</p>
            <p className="text-[11px] text-gray-500">Réservations</p>
          </div>
          <div className="ic-card !transform-none p-4 text-center">
            <CalendarCheck size={16} className="text-[#009246] mx-auto mb-2" />
            <p className="text-2xl font-semibold">{upcoming.length}</p>
            <p className="text-[11px] text-gray-500">À venir</p>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="gold-label mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Consentements RGPD</h3>
          <div className="ic-card !transform-none p-4 space-y-3 text-sm">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-gray-300">Marketing par email</span>
              <input data-testid="fiche-consent-email" type="checkbox" checked={!!user.consent_email} onChange={() => toggleConsent("consent_email")} className="accent-[#D4AF37] w-4 h-4" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-gray-300">Marketing par SMS</span>
              <input data-testid="fiche-consent-sms" type="checkbox" checked={!!user.consent_sms} onChange={() => toggleConsent("consent_sms")} className="accent-[#D4AF37] w-4 h-4" />
            </label>
            {user.consent_date && <p className="text-xs text-gray-600">Dernière mise à jour : {user.consent_date.split("T")[0]}</p>}
          </div>
        </div>

        <div className="mb-8">
          <h3 className="gold-label mb-3">Notes internes</h3>
          <textarea data-testid="fiche-notes" className="ic-input min-h-[90px]" placeholder="Allergies, préférences, VIP…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button data-testid="fiche-notes-save" onClick={saveNotes} className="btn-outline-gold !py-1.5 !px-4 text-xs mt-2"><Save size={13} /> Enregistrer les notes</button>
        </div>

        <div className="mb-8">
          <h3 className="gold-label mb-3">Historique des réservations</h3>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {reservations.length === 0 && <p className="text-gray-500 text-sm">Aucune réservation.</p>}
            {reservations.map((r) => (
              <div key={r.id} className="flex justify-between text-sm border-b border-white/5 pb-2" data-testid={`fiche-resa-${r.id}`}>
                <span className="text-gray-300">{r.date} · {r.time} · {r.party_size} pers.</span>
                <span className={STATUS_COLORS[r.status] || "text-gray-400"}>{r.status?.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-10">
          <h3 className="gold-label mb-3">Historique RITAS</h3>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {ritas_history.length === 0 && <p className="text-gray-500 text-sm">Aucune transaction.</p>}
            {ritas_history.map((t) => (
              <div key={t.id} className="flex justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-gray-300">{TX_LABELS[t.type] || t.type} — {t.description}
                  <span className="text-gray-600 text-xs ml-2">{new Date(t.created_at).toLocaleDateString("fr-FR")}</span>
                </span>
                <span className={t.amount >= 0 ? "text-[#009246]" : "text-[#ff6b81]"}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <button data-testid="fiche-gdpr-delete" onClick={deleteGdpr} className="flex items-center gap-2 text-sm text-[#ff6b81] border border-[#C8102E]/40 rounded-full px-5 py-2 hover:bg-[#C8102E]/10 transition-colors">
            <Trash2 size={14} /> Supprimer toutes les données (RGPD)
          </button>
          <p className="text-xs text-gray-600 mt-2">Supprime définitivement le compte, les réservations, les transactions RITAS et les sessions.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomerFiche;
